import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { ErrorState, Skeleton } from '@/components/ui/Feedback';
import { PageHeader } from '@/app/PlaceholderPage';
import { cn } from '@/lib/cn';
import { SCOPE_LABEL, instrumentApi, type InstrumentListItem } from './api';

/** L-13a — Daftar instrumen yang hidup di sistem. */
export function InstrumentListPage() {
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['instruments'],
    queryFn: () => instrumentApi.list(),
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Instrumen & Bobot"
        description="Alat ukur yang dipakai menilai TBM dan PELITA SLI, beserta bobot, kriteria skala, dan ambang kategorinya."
      />

      {error ? (
        <ErrorState message={(error as Error).message} onRetry={() => refetch()} />
      ) : isLoading ? (
        <div className="grid gap-4 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-44 w-full" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          {(data ?? []).map((item) => (
            <InstrumentCard
              key={item.id}
              item={item}
              onOpen={() => navigate(`/pengaturan/instrumen/${item.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function InstrumentCard({ item, onOpen }: { item: InstrumentListItem; onOpen: () => void }) {
  return (
    <button onClick={onOpen} className="text-left">
      <Card className="card-hover h-full">
        <div className="flex items-start justify-between gap-3">
          <span className="rounded-full bg-primary-soft px-2.5 py-1 text-[13px] font-medium text-primary">
            {SCOPE_LABEL[item.scope]}
          </span>
          <span
            className={cn(
              'rounded-full px-2.5 py-1 text-[13px] font-medium',
              item.status === 'published'
                ? 'bg-success-soft text-success'
                : 'bg-warn-soft text-[#9a6400]',
            )}
          >
            {item.status === 'published' ? 'Terbit' : item.status === 'draft' ? 'Draf' : 'Arsip'} · v
            {item.version}
          </span>
        </div>

        <h2 className="mt-3.5 text-[17px] font-semibold text-ink">{item.name}</h2>
        <p className="mt-1 line-clamp-3 text-[13px] text-ink-secondary">{item.description}</p>

        <p className="mt-3.5 text-sm text-ink-secondary">
          <span className="tabular font-semibold text-ink">{item.indicatorCount}</span> indikator ·
          kode <span className="tabular">{item.code}</span>
        </p>
      </Card>
    </button>
  );
}
